// PlaybookItemFocusModal.jsx

import React, { useCallback, useMemo } from 'react';

// Components
import Modal from 'components/modal/Modal';
import PlaybookItemFocus from 'node/missions/mission/focus/PlaybookItemFocus';

/**
 * PlaybookItemFocusModal
 *
 * Desktop host for the checklist-item playbook wizard.
 *
 * @param {object|null} [hostNode] - Workflow host record.
 * @param {boolean} isOpen - Whether the wizard is open.
 * @param {object|null} missionActions - Shared run action handlers.
 * @param {object|null} [missionProgress] - Optional precomputed progress.
 * @param {object|null} missionRun - Normalized playbook run.
 * @param {function} onClose - Closes the wizard and returns to the list.
 * @param {function} [onHostNodeChange] - Optional local host patch after field saves.
 * @param {Map<string, object>|null} [stepNodesByKey] - Live TASK nodes keyed by nodeKey.
 * @param {object|null} template - Playbook template.
 * @param {object|null} user - Current signed-in user.
 * @returns {ReactNode} PlaybookItemFocusModal.
 */
const PlaybookItemFocusModal = React.memo(({
  hostNode = null,
  isOpen = false,
  missionActions = null,
  missionProgress = null,
  missionRun = null,
  onClose,
  onHostNodeChange = null,
  stepNodesByKey = null,
  template = null,
  user = null,
}) => {

  const playbookTitle = useMemo(
    () => String(template?.title || '').trim() || 'Playbook',
    [template?.title]
  );

  /**
   * handleClose
   *
   * Returns to the playbook list without changing run state.
   */
  const handleClose = useCallback(() => {
    onClose?.();
  }, [onClose]);

  if (!template) return null;

  // ----- RENDER ----- //

  return (
    <Modal
      height="min(820px, 92vh)"
      iconKey="wand_stars"
      isOpen={isOpen}
      maxHeight="92vh"
      title={playbookTitle}
      width={640}
      onClose={handleClose}
    >
      <PlaybookItemFocus
        active={isOpen}
        hostNode={hostNode}
        missionActions={missionActions}
        missionProgress={missionProgress}
        missionRun={missionRun}
        stepNodesByKey={stepNodesByKey}
        template={template}
        user={user}
        onHostNodeChange={onHostNodeChange}
      />
    </Modal>
  );
});

// Dev Tools
PlaybookItemFocusModal.displayName = 'PlaybookItemFocusModal';

export default PlaybookItemFocusModal;
